import { Color, Spacing } from '@persona-id/design-tokens';
import { ArrowRightIcon } from '@persona-id/design-tokens/icons';
import { useSelector } from 'react-redux';
import { Link, matchPath, useLocation } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import Banner from 'dashboard/components-lib/Banner';
import { OrganizationPlan, OrganizationPlanKey } from 'dashboard/models/organization-plan';
import { RootState } from 'dashboard/redux/store';
import Sticker from 'design-system/components/Sticker';
import { Paragraph } from 'design-system/components/Text';

interface OrganizationPlanStatusBannerProps {
  isLoggedIn: boolean;
}

const OrganizationPlanStatusBanner = ({ isLoggedIn }: OrganizationPlanStatusBannerProps) => {
  const { pathname } = useLocation();

  // If you find yourself coming here to copy this logic, please reach out to the Growth Team as we would prefer
  // to find a better solution for this than checking the plan.
  const currentOrganizationPlan = useSelector(
    (state: RootState) => state.organization.currentOrganizationPlan,
  );

  const pendingOrganizationPlanMigration = useSelector(
    (state: RootState) => state.organization.pendingOrganizationPlanMigration,
  );

  const renderTrialSticker = (currentOrganizationPlan: OrganizationPlan) => {
    if (!currentOrganizationPlan) {
      return null;
    }
    if (currentOrganizationPlan.attributes['ends-in-days'] === 0) {
      return 'Ends today';
    }
    if (currentOrganizationPlan?.attributes?.['ends-in-days']) {
      return `${currentOrganizationPlan.attributes['ends-in-days']} days left`;
    }
    return null;
  };

  if (!isLoggedIn) {
    return null;
  }

  if (currentOrganizationPlan == null) {
    return (
      <Banner
        centered
        data-test='organization-plan-status-banner__no-current-plan'
        backgroundColor={Color.CorePurple100}
      >
        <BannerContents>
          <Paragraph color='primary'>
            We’re putting the final touches on your new Persona instance. Everything should be ready
            in a couple of minutes — try refreshing the page for updates.
          </Paragraph>
        </BannerContents>
      </Banner>
    );
  } else if (currentOrganizationPlan?.attributes?.['key'] === OrganizationPlanKey.Restricted) {
    return (
      <Banner data-test='organization-plan-status-banner__inactive-organization' centered>
        <BannerContents>
          <Paragraph size='sm' color='primary'>
            Your Persona organization is inactive. To reactivate your organization, please{' '}
            <a href='mailto:business-verification@withpersona.com'>contact support</a>.
          </Paragraph>
        </BannerContents>
      </Banner>
    );
  } else if (currentOrganizationPlan?.attributes?.['key'] === OrganizationPlanKey.Churned) {
    return (
      <Banner
        centered
        data-test='organization-plan-status-banner__inactive-organization'
        backgroundColor={Color.CorePurple100}
      >
        <BannerContents>
          <Paragraph color='primary'>Your account is currently inactive.</Paragraph>
          {!matchPath({ path: '/dashboard/billing' }, pathname) && (
            <BannerLink to='/dashboard/billing'>
              Explore plans
              <ArrowRightIcon />
            </BannerLink>
          )}
        </BannerContents>
      </Banner>
    );
  } else if (!!currentOrganizationPlan && !!pendingOrganizationPlanMigration) {
    return (
      <Banner
        centered
        data-test='organization-plan-status-banner__pending-migration'
        backgroundColor={Color.CorePurple100}
      >
        <BannerContents>
          <Sticker variant='purple'>{renderTrialSticker(currentOrganizationPlan)}</Sticker>
          <Paragraph color='primary'>
            Your organization is currently on the{' '}
            {currentOrganizationPlan.attributes['display-name']} plan. Your plan change request is
            under review.
          </Paragraph>
        </BannerContents>
      </Banner>
    );
  } else if (
    // First check is needed here because null >= 0 and null <= 30 are true in JS
    currentOrganizationPlan?.attributes?.['ends-in-days'] != null &&
    currentOrganizationPlan.attributes['ends-in-days'] <= 30
  ) {
    return (
      <Banner
        centered
        data-test='organization-plan-status-banner__trial-ending-soon'
        backgroundColor={Color.CorePurple100}
      >
        <BannerContents>
          <Sticker variant='purple'>{renderTrialSticker(currentOrganizationPlan)}</Sticker>
          <Paragraph color='primary'>
            Your organization is currently on the{' '}
            {currentOrganizationPlan.attributes['display-name']} plan.
          </Paragraph>
          {!matchPath({ path: '/dashboard/billing' }, pathname) && (
            <Paragraph color='primary'>
              <BannerLink to='/dashboard/billing'>
                Explore plans
                <ArrowRightIcon />
              </BannerLink>
            </Paragraph>
          )}
        </BannerContents>
      </Banner>
    );
  }

  return null;
};

const BannerContents = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  column-gap: ${Spacing[2]};
`;

const BannerLink = styled(Link)`
  display: flex;
  align-items: center;

  > svg {
    margin-left: ${Spacing[1]};
  }
`;

export default OrganizationPlanStatusBanner;
